<template>
  <div class="w-full h-full flex flex-col overflow-hidden bg-dark-secondary">
    <div class="max-w-[1600px] mx-auto w-full px-6 lg:px-8 py-6 lg:py-8 flex flex-col flex-1 min-h-0 gap-0">

      <!-- HEADER -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-2xl font-bold text-dark-primary tracking-tight">Dashboard</h1>
          <p class="text-sm text-dark-secondary mt-1">PAM session intelligence — forensic overview across all recordings</p>
        </div>
      </div>

      <!-- STAT CARDS -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">Total</p>
          <p class="text-3xl font-bold text-dark-primary stat-number" :class="{ 'stat-loading': loading }">{{ totalSessions }}</p>
        </div>
        <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">High Risk</p>
          <p class="text-3xl font-bold text-error stat-number" :class="{ 'stat-loading': loading }">{{ stats.highCritical }}</p>
        </div>
        <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">TTPs</p>
          <p class="text-3xl font-bold text-warning stat-number" :class="{ 'stat-loading': loading }">{{ stats.ttps }}</p>
        </div>
        <div class="bg-dark-primary border border-dark rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p class="text-[11px] text-dark-secondary font-bold uppercase tracking-wider mb-2">Completed</p>
          <p class="text-3xl font-bold text-success stat-number" :class="{ 'stat-loading': loading }">{{ stats.completed }}</p>
        </div>
      </div>

      <!-- SEARCH -->
      <div class="search-local-wrapper mb-6">
        <Search class="search-local-icon" />
        <input
          v-model="globalSearch"
          type="text"
          placeholder="Search sessions, commands, TTPs…"
          class="search-local-input"
          autocomplete="off"
          spellcheck="false"
        />
        <div v-if="searching" class="search-spinner"></div>
      </div>

      <!-- TOOLBAR -->
      <div class="flex items-center gap-2 mb-4">
        <div class="flex items-center gap-1 flex-1 min-w-0">
          <button @click="loadSessions" class="toolbar-btn" title="Reload">
            <RefreshCw class="w-4 h-4" />
          </button>
          <template v-if="selectedSessions.length > 0">
            <div class="w-px h-5 bg-dark mx-1"></div>
            <span class="text-xs text-accent font-semibold px-2">{{ selectedSessions.length }} selected</span>
            <button class="toolbar-btn" title="Delete selected" @click="deleteSelected">
              <Trash2 class="w-4 h-4 hover:text-error transition-colors" />
            </button>
          </template>
          <template v-else>
            <div class="w-px h-5 bg-dark mx-1"></div>
            <div class="flex overflow-hidden">
              <button
                v-for="tab in tabs" :key="tab.value"
                class="tab-btn"
                :class="{ active: activeTab === tab.value }"
                @click="activeTab = tab.value"
              >{{ tab.label }}</button>
            </div>
          </template>
        </div>

        <div class="flex items-center gap-1 flex-shrink-0">
          <span class="text-xs text-dark-secondary px-2 whitespace-nowrap">
            {{ pageStart }}–{{ pageEnd }} de {{ totalSessions }}
          </span>
          <button class="toolbar-btn" :disabled="page === 0" @click="prevPage">
            <ChevronLeft class="w-4 h-4" />
          </button>
          <button class="toolbar-btn" :disabled="pageEnd >= totalSessions" @click="nextPage">
            <ChevronRight class="w-4 h-4" />
          </button>
          <div class="w-px h-5 bg-dark mx-1"></div>
          <button class="toolbar-btn" :class="{ 'text-accent': viewMode === 'table' }" @click="viewMode = 'table'" title="Table">
            <Table class="w-4 h-4" />
          </button>
          <button class="toolbar-btn" :class="{ 'text-accent': viewMode === 'cards' }" @click="viewMode = 'cards'" title="Cards">
            <LayoutGrid class="w-4 h-4" />
          </button>
        </div>
      </div>

      <!-- SESSION LIST -->
      <div class="flex-1 min-h-0 flex flex-col bg-dark-primary border border-dark rounded-xl shadow-sm overflow-hidden">

        <div v-if="!loading && !filtered.length" class="flex-1 flex flex-col items-center justify-center text-center">
          <HardDrive class="w-12 h-12 text-dark-secondary opacity-40 mb-4" />
          <p class="text-sm font-medium text-dark-secondary mb-1">No sessions match the current filter</p>
          <p class="text-xs text-dark-tertiary">Try a different tab or go to Recordings to upload</p>
        </div>

        <!-- TABLE VIEW -->
        <template v-else-if="viewMode === 'table'">
          <div class="flex-1 overflow-auto custom-scrollbar" style="scrollbar-gutter: stable;">
            <table class="w-full min-w-[700px] border-collapse" style="table-layout: fixed;">
              <thead class="sticky top-0 z-10 bg-dark-primary/95 backdrop-blur-sm">
                <tr class="border-b border-dark">
                  <th class="table-th w-8 text-center">
                    <input type="checkbox" v-model="selectAll" class="w-3.5 h-3.5 cursor-pointer accent-accent" />
                  </th>
                  <th class="table-th w-6"></th>
                  <th class="table-th w-20">Session</th>
                  <th class="table-th">Summary</th>
                  <th class="table-th w-20">Protocol</th>
                  <th class="table-th w-16">Risk</th>
                  <th class="table-th w-20">Duration</th>
                  <th class="table-th w-12">TTPs</th>
                  <th class="table-th w-44">Uploaded</th>
                  <th class="table-th w-16"></th>
                </tr>
              </thead>
              <tbody :style="loading ? 'opacity: 0.4; pointer-events: none; transition: opacity 0.2s' : 'opacity: 1; transition: opacity 0.2s'">
                <tr
                  v-for="session in filtered" :key="session.id"
                  class="border-b border-dark transition cursor-pointer group"
                  :class="[
                    !sessions.isViewed(session.id) ? 'bg-dark-tertiary/20 hover:bg-dark-tertiary/40' : 'hover:bg-dark-secondary/50',
                    selectedSessions.includes(session.id) ? 'bg-accent/5' : ''
                  ]"
                  @click="openSession(session)"
                >
                  <td class="table-td text-center" @click.stop>
                    <input
                      type="checkbox" :value="session.id" v-model="selectedSessions"
                      class="w-3.5 h-3.5 cursor-pointer accent-accent transition-opacity"
                      :class="selectedSessions.includes(session.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'"
                    />
                  </td>
                  <td class="table-td text-center" @click.stop="toggleStar(session.id)">
                    <Star
                      class="w-4 h-4 transition-colors"
                      :class="starredSessions.has(session.id) ? 'fill-current text-warning' : 'text-dark-tertiary group-hover:text-dark-secondary'"
                    />
                  </td>
                  <td class="table-td max-w-[80px]">
                    <p class="text-sm truncate" :class="!sessions.isViewed(session.id) ? 'font-extrabold text-dark-primary' : 'font-medium text-dark-secondary'">
                      {{ session.filename || session.id }}
                    </p>
                  </td>
                  <td class="table-td max-w-0">
                    <p v-if="verdictSnippet(session)" class="text-sm truncate" :class="!sessions.isViewed(session.id) ? 'font-bold text-dark-primary' : 'text-dark-secondary'">
                      {{ verdictSnippet(session) }}
                    </p>
                    <p v-else class="text-xs text-dark-tertiary italic">No summary available</p>
                  </td>
                  <td class="table-td">
                    <span class="session-tag" :class="'tag-' + protocolLabel(session).toLowerCase()">{{ protocolLabel(session) }}</span>
                  </td>
                  <td class="table-td">
                    <span v-if="session.risk" class="session-tag" :class="riskTag(session.risk)">{{ riskShort(session.risk) }}</span>
                    <span v-else class="text-xs text-dark-secondary">—</span>
                  </td>
                  <td class="table-td">
                    <span class="text-sm" :class="!sessions.isViewed(session.id) ? 'font-extrabold text-dark-primary' : 'text-dark-secondary'">
                      {{ formatDuration(session.guac?.duration_ms) }}
                    </span>
                  </td>
                  <td class="table-td">
                    <span v-if="ttpCount(session)" class="text-sm text-error" :class="!sessions.isViewed(session.id) ? 'font-[800]' : 'font-medium'">{{ ttpCount(session) }}</span>
                    <span v-else class="text-xs text-dark-secondary">—</span>
                  </td>
                  <td class="table-td whitespace-nowrap">
                    <span class="text-sm" :class="!sessions.isViewed(session.id) ? 'font-extrabold text-dark-primary' : 'text-dark-secondary'">
                      {{ formatDate(session.uploaded_at) }}
                    </span>
                  </td>
                  <td class="table-td" @click.stop>
                    <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button @click="deleteSession(session)" class="action-btn danger" title="Delete">
                        <Trash2 class="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <!-- CARDS VIEW -->
        <div v-else class="flex-1 overflow-y-auto custom-scrollbar" :style="loading ? 'opacity: 0.4; pointer-events: none; transition: opacity 0.2s' : 'opacity: 1; transition: opacity 0.2s'">
          <div class="grid gap-4 p-6" style="grid-template-columns: repeat(auto-fill, minmax(220px, 1fr))">
            <div v-for="session in filtered" :key="session.id" @click="openSession(session)">
              <SessionCard :session="session" />
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import {
  HardDrive, Search, Table, LayoutGrid,
  ChevronLeft, ChevronRight, RefreshCw, Trash2, Star,
} from "lucide-vue-next";
import SessionCard from "./SessionCard.vue";
import { apiClient } from "../services/apiClient";
import { useSessionStore } from "../stores/sessionStore";
import { useAuthStore }    from "../stores/authStore";

const router    = useRouter();
const sessions  = useSessionStore();
const authStore = useAuthStore();

// Pre-populate from store so cards don't flash zero on mount
const allSessions   = ref(sessions.sessions.filter(s => s.status === "completed"));
const totalSessions = ref(allSessions.value.length);
const loading       = ref(true);
const searching     = ref(false);
const globalSearch  = ref("");
const activeTab     = ref("all");
const viewMode      = ref(localStorage.getItem("dashboard_view") ?? "table");
const page          = ref(0);
const pageSize      = 50;
let   searchDebounce = null;

const selectedSessions = ref([]);
const starredSessions  = ref(new Set(JSON.parse(localStorage.getItem("starred_sessions") || "[]")));

const selectAll = computed({
  get() { return filtered.value.length > 0 && selectedSessions.value.length === filtered.value.length; },
  set(val) { selectedSessions.value = val ? filtered.value.map((s) => s.id) : []; },
});

function toggleStar(id) {
  if (starredSessions.value.has(id)) starredSessions.value.delete(id);
  else starredSessions.value.add(id);
  localStorage.setItem("starred_sessions", JSON.stringify([...starredSessions.value]));
}

const pageStart = computed(() => Math.min(page.value * pageSize + 1, totalSessions.value || 1));
const pageEnd   = computed(() => Math.min((page.value + 1) * pageSize, totalSessions.value));

function prevPage() { if (page.value > 0) { page.value--; loadSessions(); } }
function nextPage() { if (pageEnd.value < totalSessions.value) { page.value++; loadSessions(); } }

const tabs = [
  { label: "ALL",   value: "all"      },
  { label: "HIGH",  value: "critical" },
  { label: "TTPs",  value: "ttps"     },
  { label: "TODAY", value: "today"    },
  { label: "SSH",   value: "ssh"      },
  { label: "RDP",   value: "rdp"      },
  { label: "VNC",   value: "vnc"      },
];

watch(viewMode, (v) => localStorage.setItem("dashboard_view", v));
watch(activeTab, () => { page.value = 0; selectedSessions.value = []; loadSessions(); });
watch(globalSearch, () => {
  clearTimeout(searchDebounce);
  searching.value = true;
  searchDebounce = setTimeout(() => { page.value = 0; selectedSessions.value = []; loadSessions(true); }, 300);
});

// Reload when tenant resolves (apiBase set after mount) or workspace switches
watch(() => authStore.apiBase, (base) => {
  page.value = 0;
  if (base) loadSessions();
});

async function loadSessions(isSearch = false) {
  if (!isSearch) loading.value = true;
  searching.value = isSearch;
  try {
    const params = new URLSearchParams({ limit: pageSize, offset: page.value * pageSize, sort: "uploaded_at", order: "desc" });
    // Dashboard only shows completed sessions
    params.set("status", "completed");
    if (activeTab.value === "critical") params.set("risk", "high");
    if (activeTab.value === "today") {
      const d = new Date(); d.setHours(0, 0, 0, 0);
      params.set("from", d.toISOString().split("T")[0]);
    }
    const res  = await apiClient(`/sessions?${params}`);
    let data   = res?.data ?? [];
    if (activeTab.value === "ttps") data = data.filter((s) => (s.llm?.detections ?? 0) > 0);
    if (activeTab.value === "ssh")  data = data.filter((s) => protocolLabel(s) === "SSH");
    if (activeTab.value === "rdp")  data = data.filter((s) => protocolLabel(s) === "RDP");
    if (activeTab.value === "vnc")  data = data.filter((s) => protocolLabel(s) === "VNC");
    const q = globalSearch.value.trim().toLowerCase();
    if (q) data = data.filter((s) =>
      s.id.toLowerCase().includes(q) ||
      (s.filename ?? "").toLowerCase().includes(q) ||
      (s.verdict  ?? "").toLowerCase().includes(q)
    );
    allSessions.value   = data;
    totalSessions.value = res?.total ?? data.length;
  } catch (err) {
    console.error("[Dashboard]", err);
  } finally {
    loading.value   = false;
    searching.value = false;
  }
}

const filtered = computed(() => allSessions.value);

const stats = computed(() => ({
  highCritical: allSessions.value.filter((s) => ["high", "critical"].includes((s.risk ?? "").toLowerCase())).length,
  ttps:         allSessions.value.reduce((acc, s) => acc + (s.llm?.detections ?? 0), 0),
  completed:    allSessions.value.filter((s) => s.status === "completed").length,
}));

function openSession(session) {
  sessions.selectSession(session);
  router.push({ name: "investigation", params: { id: session.id } });
}

async function deleteSession(session) {
  if (!confirm(`Delete session "${session.filename}"?\nThis cannot be undone.`)) return;
  try {
    await apiClient(`/sessions/${session.id}`, { method: "DELETE" });
    allSessions.value = allSessions.value.filter((s) => s.id !== session.id);
  } catch (err) {
    console.error("[Dashboard] Delete failed:", err);
  }
}

async function deleteSelected() {
  if (!selectedSessions.value.length) return;
  if (!confirm(`Delete ${selectedSessions.value.length} session(s)? This cannot be undone.`)) return;
  for (const id of selectedSessions.value) {
    try { await apiClient(`/sessions/${id}`, { method: "DELETE" }); } catch {}
  }
  selectedSessions.value = [];
  loadSessions();
}

function protocolLabel(s) {
  const id = (s.id ?? "").toLowerCase();
  const fn = (s.filename ?? "").toLowerCase();
  if (fn.includes("ssh") || id.includes("ssh")) return "SSH";
  if (fn.includes("vnc") || id.includes("vnc")) return "VNC";
  return "RDP";
}

function riskShort(risk) {
  return { critical: "CRIT", high: "HIGH", medium: "MED", low: "LOW" }[risk?.toLowerCase()] ?? risk;
}

function riskTag(risk) {
  return { critical: "tag-critical", high: "tag-high", medium: "tag-medium", low: "tag-low" }[(risk ?? "").toLowerCase()] ?? "";
}

function ttpCount(s)       { return s.llm?.detections ?? 0; }
function verdictSnippet(s) { return s.verdict ?? null; }

function formatDate(ts) {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms) {
  if (!ms) return "—";
  const t = Math.floor(ms / 1000);
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

// WS: reload when a session completes
function onWsCompleted() { setTimeout(() => loadSessions(), 500); }

onMounted(() => {
  loadSessions();
  window.addEventListener("ws:session-completed", onWsCompleted);
});
onUnmounted(() => {
  window.removeEventListener("ws:session-completed", onWsCompleted);
});
</script>

<style scoped>
.stat-number {
  transition: opacity 0.2s ease;
}
.stat-loading {
  opacity: 0.35;
}
.search-local-wrapper { position: relative; display: flex; align-items: center; width: 100%; }
input[type="text"].search-local-input {
  width: 100%; height: 2.5rem;
  padding: 0.625rem 1rem 0.625rem 2.5rem;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-color) !important;
  border-radius: 0.5rem !important;
  font-size: 0.875rem; color: var(--text-primary);
  transition: all 0.2s ease; outline: none !important; box-shadow: none !important;
}
input[type="text"].search-local-input::placeholder { color: var(--text-secondary); }
input[type="text"].search-local-input:focus { border-color: var(--accent) !important; }
.search-local-icon { position: absolute; left: 0.75rem; width: 1rem; height: 1rem; color: var(--text-secondary); pointer-events: none; z-index: 10; }
.search-spinner { position: absolute; right: 0.75rem; width: 14px; height: 14px; border: 2px solid var(--border-color); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.6s linear infinite; }

.toolbar-btn { display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; background: transparent; border: none; border-radius: 50%; color: var(--text-secondary); cursor: pointer; transition: background 0.15s; }
.toolbar-btn:hover:not(:disabled) { background: var(--hover-bg); color: var(--text-primary); }
.toolbar-btn:disabled { opacity: 0.3; cursor: default; }

.tab-btn { flex: 0 0 58px; text-align: center; padding: 6px 0; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; color: var(--text-secondary); background: none; border: none; border-bottom: 2px solid transparent; cursor: pointer; transition: all 0.15s; white-space: nowrap; border-radius: 6px 6px 0 0; }
.tab-btn:hover  { color: var(--text-primary); background: var(--hover-bg); }
.tab-btn.active { color: var(--text-primary); border-bottom-color: var(--accent); background: transparent; }

.table-th { padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-secondary); white-space: nowrap; overflow: hidden; }
.table-td { padding: 14px 16px; vertical-align: middle; overflow: hidden; }

.action-btn { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 6px; border: 1px solid var(--border-color); background: transparent; color: var(--text-secondary); cursor: pointer; transition: all 0.15s ease; }
.action-btn:hover { color: var(--text-primary); background: var(--bg-quaternary); }
.action-btn.danger:hover { color: var(--color-error, #f87171); border-color: var(--color-error, #f87171); background: rgba(248,113,113,0.1); }

.session-tag { display: inline-flex; align-items: center; padding: 2px 6px; font-size: 10px; font-weight: 700; border-radius: 3px; border: 1px solid; text-transform: uppercase; letter-spacing: 0.5px; }
.tag-ssh      { background: rgba(78,201,176,.12);  border-color: rgba(78,201,176,.35);  color: #6dd9bf; }
.tag-rdp      { background: rgba(86,156,214,.12);  border-color: rgba(86,156,214,.35);  color: #7db3e8; }
.tag-vnc      { background: rgba(197,134,192,.12); border-color: rgba(197,134,192,.35); color: #d8a8d3; }
.tag-low      { background: rgba(34,197,94,.15);   border-color: rgba(34,197,94,.4);    color: #22c55e; }
.tag-medium   { background: rgba(245,158,11,.15);  border-color: rgba(245,158,11,.4);   color: #f59e0b; }
.tag-high     { background: rgba(239,68,68,.15);   border-color: rgba(239,68,68,.4);    color: #ef4444; }
.tag-critical { background: rgba(220,38,38,.2);    border-color: rgba(220,38,38,.5);    color: #dc2626; }

.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.custom-scrollbar { scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.1) transparent; }
.custom-scrollbar::-webkit-scrollbar       { width: 6px; height: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
</style>